/**
 * PLANCHE LOGO — /logo
 * Pistes de monogramme, de logotype et de version verrouillée.
 * Tout est en SVG : net du favicon 16 px à l'affiche, et modifiable au pixel.
 * Le vert sauge et le point de signature sont conservés.
 */
import React from "react";

const SAUGE = "#37544A";
const CREME = "#F7F7F5";
const ENCRE = "#141413";
const SERIF = "'Playfair Display', Georgia, serif";

/* ---------------------------------------------------------- monogrammes */

/** A — Sceau plein. Le plus lisible en photo de profil. */
export function MonoSceau({ taille = 88, fond = SAUGE, lettre = CREME }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 100 100" role="img" aria-label="Servae">
      <circle cx="50" cy="50" r="50" fill={fond} />
      <text
        x="50"
        y="50"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={SERIF}
        fontWeight="600"
        fontSize="62"
        fill={lettre}
      >
        S
      </text>
      <circle cx="72" cy="70" r="4.6" fill={lettre} opacity="0.9" />
    </svg>
  );
}

/** B — Icône d'application. Carré adouci, point en haut à droite. */
export function MonoCarre({ taille = 88, fond = SAUGE, lettre = CREME }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 100 100" role="img" aria-label="Servae">
      <rect x="0" y="0" width="100" height="100" rx="24" fill={fond} />
      <text
        x="50"
        y="52"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={SERIF}
        fontWeight="600"
        fontSize="60"
        fill={lettre}
      >
        S
      </text>
      <circle cx="78" cy="22" r="5" fill={lettre} opacity="0.85" />
    </svg>
  );
}

/** C — Anneau. Le point est posé sur l'orbite. */
export function MonoAnneau({ taille = 88, trait = SAUGE, lettre = SAUGE }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 100 100" role="img" aria-label="Servae">
      <circle cx="50" cy="50" r="46" fill="none" stroke={trait} strokeWidth="2" />
      <text
        x="50"
        y="51"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={SERIF}
        fontWeight="600"
        fontSize="56"
        fill={lettre}
      >
        S
      </text>
      <circle cx="82.5" cy="82.5" r="5.5" fill={trait} />
    </svg>
  );
}

/** D — Négatif. Le S est évidé dans la forme : très fort en petit. */
export function MonoNegatif({ taille = 88, fond = SAUGE }) {
  const id = React.useId();
  return (
    <svg width={taille} height={taille} viewBox="0 0 100 100" role="img" aria-label="Servae">
      <defs>
        <mask id={id}>
          <rect x="0" y="0" width="100" height="100" fill="#fff" />
          <text
            x="50"
            y="52"
            textAnchor="middle"
            dominantBaseline="central"
            fontFamily={SERIF}
            fontWeight="700"
            fontSize="68"
            fill="#000"
          >
            S
          </text>
          <circle cx="79" cy="21" r="5" fill="#000" />
        </mask>
      </defs>
      <rect x="0" y="0" width="100" height="100" rx="24" fill={fond} mask={`url(#${id})`} />
    </svg>
  );
}

/** E — Lettre nue, point intégré au terminal. Sans conteneur. */
export function MonoNu({ taille = 88, lettre = ENCRE, point = SAUGE }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 100 100" role="img" aria-label="Servae">
      <text
        x="42"
        y="52"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={SERIF}
        fontWeight="600"
        fontSize="86"
        fill={lettre}
      >
        S
      </text>
      <circle cx="76" cy="74" r="7" fill={point} />
    </svg>
  );
}

/** F — Estampille. Sceau complet : la lettre, un filet, le mot en tout petit. */
export function MonoEstampille({ taille = 88, fond = SAUGE, lettre = CREME }) {
  return (
    <svg width={taille} height={taille} viewBox="0 0 100 100" role="img" aria-label="Servae">
      <circle cx="50" cy="50" r="50" fill={fond} />
      <circle cx="50" cy="50" r="43" fill="none" stroke={lettre} strokeWidth="1" opacity="0.45" />
      <text
        x="50"
        y="44"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={SERIF}
        fontWeight="600"
        fontSize="46"
        fill={lettre}
      >
        S
      </text>
      <line x1="38" y1="66" x2="62" y2="66" stroke={lettre} strokeWidth="1" opacity="0.5" />
      <text
        x="50"
        y="77"
        textAnchor="middle"
        fontFamily="Figtree, sans-serif"
        fontWeight="700"
        fontSize="9.5"
        letterSpacing="2.4"
        fill={lettre}
      >
        SERVA
      </text>
    </svg>
  );
}

/* ----------------------------------------------------------- logotypes */

function Mot({ h = 44, couleur = ENCRE, point = SAUGE, variante = 1 }) {
  const base = { fontFamily: SERIF, fill: couleur };
  if (variante === 1)
    return (
      <svg height={h} viewBox="0 0 178 72" role="img" aria-label="Servae">
        <text x="0" y="52" {...base} fontWeight="600" fontSize="56" letterSpacing="-1.5">
          Servae
        </text>
        <circle cx="167" cy="46" r="6" fill={point} />
      </svg>
    );
  if (variante === 2)
    return (
      <svg height={h} viewBox="0 0 160 72" role="img" aria-label="Servae">
        <text x="0" y="52" {...base} fontWeight="600" fontSize="56" letterSpacing="-1.5">
          Servae
        </text>
        <circle cx="141" cy="14" r="5.5" fill={point} />
      </svg>
    );
  if (variante === 3)
    return (
      <svg height={h} viewBox="0 0 262 72" role="img" aria-label="Servae">
        <text x="0" y="50" {...base} fontWeight="500" fontSize="42" letterSpacing="11">
          SERVA
        </text>
        <circle cx="251" cy="36" r="5" fill={point} />
      </svg>
    );
  if (variante === 4)
    return (
      <svg height={h} viewBox="0 0 176 72" role="img" aria-label="Servae">
        <text
          x="0"
          y="52"
          {...base}
          fontWeight="500"
          fontStyle="italic"
          fontSize="56"
          letterSpacing="-1"
        >
          Servae
        </text>
        <circle cx="165" cy="46" r="6" fill={point} />
      </svg>
    );
  return (
    <svg height={h} viewBox="0 0 160 78" role="img" aria-label="Servae">
      <text x="0" y="52" {...base} fontWeight="700" fontSize="54" letterSpacing="-2">
        Servae
      </text>
      <line x1="2" y1="64" x2="152" y2="64" stroke={point} strokeWidth="3" />
    </svg>
  );
}

/* ---------------------------------------------------------- la planche */

function Case({ code, titre, note, fond, children }) {
  return (
    <div
      style={{
        background: fond,
        border: "1px solid rgba(20,20,19,0.10)",
        borderRadius: 16,
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 18,
        minHeight: 190,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          className="num"
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: fond === SAUGE ? "rgba(247,247,245,0.7)" : "var(--text-subtle)",
          }}
        >
          {code}
        </span>
        <span
          className="micro"
          style={{ color: fond === SAUGE ? "rgba(247,247,245,0.9)" : "var(--text-muted)" }}
        >
          {titre}
        </span>
      </div>
      <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 22, flexWrap: "wrap" }}>
        {children}
      </div>
      {note && (
        <p
          style={{
            fontSize: 12.5,
            lineHeight: 1.45,
            color: fond === SAUGE ? "rgba(247,247,245,0.72)" : "var(--text-subtle)",
          }}
        >
          {note}
        </p>
      )}
    </div>
  );
}

export default function Logos() {
  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }} data-testid="page-logos">
      <div className="colonne pad py-14">
        <p className="micro">Planche logo</p>
        <h1 className="display text-[34px] md:text-[44px] mt-2">Six pistes. Tu en gardes une.</h1>
        <div
          className="inline-flex items-center gap-3 mt-5 px-4 py-3"
          style={{ background: "var(--accent-soft)", borderRadius: "var(--radius-lg)" }}
        >
          <MonoSceau taille={30} />
          <span className="text-[14px] font-semibold" style={{ color: "var(--accent)" }}>
            Retenu : A + 2 — déployé partout (en-tête, favicon, icône d'écran d'accueil, e-mails).
          </span>
        </div>
        <p className="text-[16px] mt-4 max-w-[56ch]" style={{ color: "var(--text-muted)" }}>
          Serif dans l'esprit Playfair, point vert conservé, tout en SVG. Chaque piste est
          montrée sur crème et sur sauge, puis en très petit (la taille d'un favicon, c'est là
          que les mauvais logos meurent).
        </p>

        {/* -------------------------------------------------- monogrammes */}
        <h2 className="display text-[24px] mt-14 mb-5">Monogrammes</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: 16,
          }}
        >
          <Case code="A" titre="Sceau plein" fond={CREME} note="Le plus direct. Marche partout, même en 16 px.">
            <MonoSceau />
            <MonoSceau taille={44} />
            <MonoSceau taille={24} />
            <MonoSceau taille={16} />
          </Case>
          <Case code="A" titre="Sceau — inversé" fond={SAUGE}>
            <MonoSceau fond={CREME} lettre={SAUGE} />
            <MonoSceau taille={44} fond={CREME} lettre={SAUGE} />
            <MonoSceau taille={24} fond={CREME} lettre={SAUGE} />
          </Case>

          <Case code="B" titre="Icône d'application" fond={CREME} note="Le carré adouci se pose bien sur un écran d'accueil.">
            <MonoCarre />
            <MonoCarre taille={44} />
            <MonoCarre taille={24} />
            <MonoCarre taille={16} />
          </Case>
          <Case code="B" titre="Icône — inversée" fond={SAUGE}>
            <MonoCarre fond={CREME} lettre={SAUGE} />
            <MonoCarre taille={44} fond={CREME} lettre={SAUGE} />
            <MonoCarre taille={24} fond={CREME} lettre={SAUGE} />
          </Case>

          <Case code="C" titre="Anneau" fond={CREME} note="Le point devient un satellite. Élégant en grand, fragile en très petit.">
            <MonoAnneau />
            <MonoAnneau taille={44} />
            <MonoAnneau taille={24} />
            <MonoAnneau taille={16} />
          </Case>
          <Case code="C" titre="Anneau — inversé" fond={SAUGE}>
            <MonoAnneau trait={CREME} lettre={CREME} />
            <MonoAnneau taille={44} trait={CREME} lettre={CREME} />
            <MonoAnneau taille={24} trait={CREME} lettre={CREME} />
          </Case>

          <Case code="D" titre="Négatif" fond={CREME} note="La lettre est évidée : le fond traverse le logo. Très graphique.">
            <MonoNegatif />
            <MonoNegatif taille={44} />
            <MonoNegatif taille={24} />
            <MonoNegatif taille={16} />
          </Case>
          <Case code="D" titre="Négatif — inversé" fond={SAUGE}>
            <MonoNegatif fond={CREME} />
            <MonoNegatif taille={44} fond={CREME} />
            <MonoNegatif taille={24} fond={CREME} />
          </Case>

          <Case code="E" titre="Lettre nue" fond={CREME} note="Aucun conteneur : le S respire, le point ponctue. Le plus haut de gamme, le moins visible en photo de profil.">
            <MonoNu />
            <MonoNu taille={44} />
            <MonoNu taille={24} />
          </Case>
          <Case code="E" titre="Lettre nue — inversée" fond={SAUGE}>
            <MonoNu lettre={CREME} point={CREME} />
            <MonoNu taille={44} lettre={CREME} point={CREME} />
            <MonoNu taille={24} lettre={CREME} point={CREME} />
          </Case>

          <Case code="F" titre="Estampille" fond={CREME} note="Le sceau complet : lettre, filet, nom. Superbe en grand, illisible en favicon (à coupler avec A).">
            <MonoEstampille />
            <MonoEstampille taille={56} />
            <MonoEstampille taille={32} />
          </Case>
          <Case code="F" titre="Estampille — inversée" fond={SAUGE}>
            <MonoEstampille fond={CREME} lettre={SAUGE} />
            <MonoEstampille taille={56} fond={CREME} lettre={SAUGE} />
          </Case>
        </div>

        {/* ---------------------------------------------------- logotypes */}
        <h2 className="display text-[24px] mt-16 mb-5">Logotypes</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          <Case code="1" titre="Point posé" fond={CREME} note="La version d'aujourd'hui, resserrée.">
            <Mot variante={1} />
          </Case>
          <Case code="2" titre="Point suspendu" fond={CREME} note="Le point monte au-dessus du mot : plus aérien, plus signé.">
            <Mot variante={2} />
          </Case>
          <Case code="3" titre="Capitales espacées" fond={CREME} note="Institut, parfumerie, sérieux. Moins chaleureux.">
            <Mot variante={3} />
          </Case>
          <Case code="4" titre="Italique" fond={CREME} note="Une signature. Attention : moins lisible en petit.">
            <Mot variante={4} />
          </Case>
          <Case code="5" titre="Souligné sauge" fond={CREME} note="Le point devient un trait : plus affirmé, plus produit.">
            <Mot variante={5} />
          </Case>
          <Case code="1" titre="Sur sauge" fond={SAUGE}>
            <Mot variante={1} couleur={CREME} point={CREME} />
          </Case>
        </div>

        {/* --------------------------------------------------- verrouillé */}
        <h2 className="display text-[24px] mt-16 mb-5">Versions verrouillées</h2>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 16,
          }}
        >
          <Case code="A+1" titre="Horizontal" fond={CREME} note="Pour le haut de la page et les e-mails.">
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <MonoSceau taille={44} />
              <Mot variante={1} h={34} />
            </div>
          </Case>
          <Case code="A+1" titre="Horizontal — sauge" fond={SAUGE}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <MonoSceau taille={44} fond={CREME} lettre={SAUGE} />
              <Mot variante={1} h={34} couleur={CREME} point={CREME} />
            </div>
          </Case>
          <Case code="B+2" titre="Empilé" fond={CREME} note="Pour un tampon, une carte, une étiquette.">
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
              <MonoCarre taille={56} />
              <Mot variante={2} h={30} />
            </div>
          </Case>
        </div>

        <p className="text-[14px] mt-14" style={{ color: "var(--text-muted)" }}>
          Dis-moi la lettre du monogramme et le numéro du logotype (par exemple « A + 2 »).
          Je l'applique ensuite partout : en-tête de l'app, favicon, icône d'écran d'accueil,
          e-mails, page de réservation, espace client.
        </p>
      </div>
    </div>
  );
}
